import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Chip, Typography } from '@mui/material';
import { Check, CheckCircle } from '@mui/icons-material';
import SegmentedControl from './SegmentedControl';
import FeatureInfo from './FeatureInfo';

// The page's own order: it shows the first period a plan actually sells, and
// its toggle starts at monthly. A yearly-only plan therefore lands on yearly.
const PERIODS = ['monthly', 'quarterly', 'yearly'];
const PERIOD_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

/**
 * The plan card as the pricing page will render it, drawn from the draft.
 *
 * Editing this config used to be blind: you changed a subtitle or a price and
 * found out what it looked like by loading the marketing site. This is not
 * pixel-identical to that page, but it answers the question the fields cannot
 * on their own - how the card reads once it is assembled.
 */
const PlanCardPreview = ({ plan, billing, region: defaultRegion = 'IN' }) => {
    const theme = useTheme();
    const [region, setRegion] = React.useState(defaultRegion);
    const [periodChoice, setPeriodChoice] = React.useState(null);

    // Regions this plan is priced in. A plan can be priced somewhere it is not
    // sold — EU has never launched — and the preview still needs to show it,
    // because that is the copy an editor is checking.
    const regions = React.useMemo(
        () => Object.keys(plan?.pricing || plan?.razorpay_config || {}),
        [plan],
    );

    // Falls back when the chosen region is not priced on the plan being viewed,
    // so switching plans never leaves the preview on a region with no prices.
    const activeRegion = regions.includes(region) ? region : (regions[0] || defaultRegion);

    if (!plan) return null;

    const pricing = plan.pricing?.[activeRegion] || {};

    // The periods this plan sells in this region. A legacy plan sells three and
    // is priced differently in each, so a preview fixed to one of them can only
    // answer a third of the question.
    const availablePeriods = PERIODS.filter((p) =>
        plan.razorpay_config?.[activeRegion]?.[p]?.plan_id && pricing[p]);
    const sellablePeriods = availablePeriods.length ? availablePeriods : PERIODS.filter((p) => pricing[p]);

    const period = sellablePeriods.includes(periodChoice) ? periodChoice : sellablePeriods[0];
    const amount = period ? pricing[period] : 0;
    const hidden = plan.visible === false || plan.enabled === false;

    // Only the current generation is sold as a monthly figure billed yearly. A
    // legacy plan sells several periods, each priced for the period itself, so
    // dividing its yearly price by twelve would advertise a monthly cost nobody
    // is offered. The page gates this on the same fact, sent as
    // `current_generation`.
    const isCurrentGeneration = Boolean(plan.replaces) || /_v\d+$/.test(String(plan.plan_type || ''));
    const showMonthlyEquivalent = isCurrentGeneration && period === 'yearly' && amount > 0;
    const monthly = showMonthlyEquivalent ? Math.round(amount / 12) : amount;

    // Mirrors what the pricing endpoint derives from the same tax rates, so the
    // preview says "includes GST" exactly where the page will.
    const taxRate = Number(billing?.tax_rates?.[activeRegion] ?? 0);
    const taxNote = taxRate > 0 ? (activeRegion === 'IN' ? 'includes GST' : 'includes tax') : null;

    return (
        <Box sx={{ position: 'sticky', top: 0 }}>
            {/* The heading and the state of the plan; the controls that change
                what is being previewed sit below it, each behind its own label.
                Right-aligning two switches of different widths made a staircase,
                and neither said what it changed. */}
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} marginBottom="0.75rem">
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.4, color: theme.palette.customColors.g300 }}>
                    Preview
                </Typography>
                {hidden && <Chip size="small" label="Hidden" sx={{ borderRadius: '4px', height: 20 }} />}
            </Box>

            {(regions.length > 1 || sellablePeriods.length > 1) && (
                <Box display="grid" gap="0.5rem" marginBottom="1.25rem">
                    {/* Prices differ per region in both amount and currency, so the
                        card cannot be checked from one region alone. */}
                    {regions.length > 1 && (
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography sx={{
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: theme.palette.customColors.g500,
                                minWidth: '3.75rem',
                            }}>
                                Currency
                            </Typography>
                            <SegmentedControl
                                ariaLabel="Preview currency"
                                value={activeRegion}
                                onChange={setRegion}
                                options={regions.map((item) => ({
                                    value: item,
                                    label: plan.pricing?.[item]?.currency || item,
                                }))}
                            />
                        </Box>
                    )}

                    {/* The page's own billing toggle. Only shown when the plan sells
                        more than one period — the current plans sell yearly alone,
                        and a switch with one option is just a label. */}
                    {sellablePeriods.length > 1 && (
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography sx={{
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: theme.palette.customColors.g500,
                                minWidth: '3.75rem',
                            }}>
                                Billing
                            </Typography>
                            <SegmentedControl
                                ariaLabel="Preview billing period"
                                value={period}
                                onChange={setPeriodChoice}
                                options={sellablePeriods.map((item) => ({
                                    value: item,
                                    label: PERIOD_LABELS[item],
                                }))}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* Built from the marketing card's own values, read off
                backrr-main/pricing-v2/PricingCards.js and its stylesheets:
                18px radius with a gradient border-box for the popular plan,
                heading__5 / description__3 type, a 45px .button-primary, and
                20px feature ticks — filled for the popular card, plain
                otherwise, matching icon_91 and icon_92.

                The clamp()s on the real page scale with viewport width; this
                column is narrower than the page, so the sizes here are their
                upper bound. Lato is the page's face and is not loaded in this
                app, so the letterforms still differ. */}
            <Box sx={{
                position: 'relative',
                opacity: hidden ? 0.55 : 1,
                backgroundColor: '#fff',
                borderRadius: '18px',
                // Trimmed from the page's 1.5rem: the card is previewed in a
                // narrow column where that much dead space at top and bottom
                // costs more than the fidelity is worth.
                paddingBlock: '0.75rem',
                paddingInline: 0,
                border: plan.popular ? '2px solid transparent' : '1px solid #0046ff',
                ...(plan.popular ? {
                    background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #0046ff 0%, #CC5ADF 100%) border-box',
                } : {}),
            }}>
                {plan.popular && (
                    <Box sx={{
                        position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)',
                        padding: '0.5rem 0.75rem', borderRadius: '13px',
                        background: 'linear-gradient(90deg, #7852f8, #84aef6)',
                        lineHeight: 1,
                    }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                            Most Popular
                        </Typography>
                    </Box>
                )}

                <Box sx={{ padding: '1rem' }}>
                    <Box sx={{ marginBottom: '1.5rem' }}>
                        <Typography sx={{ fontSize: '1.333rem', fontWeight: 700, lineHeight: 1.4, color: '#212529' }}>
                            {plan.title || 'Untitled plan'}
                        </Typography>
                        <Typography sx={{ fontSize: '1.035rem', lineHeight: 1.31, color: '#212529', minHeight: '1.35rem' }}>
                            {plan.subtitle}
                        </Typography>
                    </Box>

                    <Box sx={{ marginBottom: '0.5rem' }}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                            {amount ? (
                                <>
                                    <Box display="flex" alignItems="baseline">
                                        <Typography sx={{ fontSize: '1.5rem', color: '#212529' }}>{pricing.symbol || ''}</Typography>
                                        <Typography sx={{ fontSize: '3rem', fontWeight: 700, lineHeight: 1.2, color: '#212529' }}>
                                            {monthly.toLocaleString('en-IN')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ paddingTop: '0.8rem', lineHeight: 1.35, fontSize: '0.75rem' }}>
                                        <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: '#212529' }}>
                                            {`${pricing.currency || ''} / ${showMonthlyEquivalent ? 'month' : period.replace('ly', '')}`}
                                        </Typography>
                                        {/* Only a monthly-equivalent price needs the real
                                            charge spelled out; a plan priced for its own
                                            period would just repeat the line above. */}
                                        {showMonthlyEquivalent && (
                                            <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: '#6c757d' }}>
                                                {`billed yearly${taxNote ? ` (${taxNote})` : ''}`}
                                            </Typography>
                                        )}
                                    </Box>
                                </>
                            ) : (
                                <Typography sx={{ fontSize: '3rem', fontWeight: 700, lineHeight: 1.2, color: '#212529' }}>
                                    Free
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ marginBottom: '1.5rem' }}>
                        <Box sx={{
                            height: '45px',
                            borderRadius: '10px',
                            background: 'linear-gradient(90deg, #7852f8, #84aef6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Typography sx={{ fontSize: '1.083rem', fontWeight: 600, lineHeight: 1.4, color: '#fff' }}>
                                {plan.buttonText || 'Subscribe'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="grid" gap="0.5rem">
                        {(plan.features || []).map((feature, index) => (
                            <Box key={index} display="flex" alignItems="flex-start" gap={1}>
                                {/* No tick means no space for one: the pages collapse
                                    the icon entirely, so the text starts at the card's
                                    own left edge. */}
                                {feature.hide_icon ? null : feature.disabled ? (
                                    <Box sx={{
                                        width: 20, height: 20, flexShrink: 0, marginTop: '2px',
                                        borderRadius: '50%', backgroundColor: '#f5f5f5',
                                    }} />
                                ) : plan.popular ? (
                                    <CheckCircle sx={{ fontSize: 20, flexShrink: 0, marginTop: '2px', color: '#0046ff' }} />
                                ) : (
                                    <Check sx={{ fontSize: 20, flexShrink: 0, marginTop: '2px', color: '#212529' }} />
                                )}
                                <Typography component="div" sx={{
                                    fontSize: '1.035rem',
                                    lineHeight: 1.31,
                                    color: feature.disabled ? '#adb5bd' : '#212529',
                                    fontWeight: feature.highlight ? 600 : 400,
                                    background: feature.highlight ? 'linear-gradient(135deg,#0046ff,#cc5adf)' : 'none',
                                    WebkitBackgroundClip: feature.highlight ? 'text' : 'none',
                                    WebkitTextFillColor: feature.highlight ? 'transparent' : 'currentColor',
                                }}>
                                    {feature.text || <span style={{ color: '#adb5bd' }}>(empty feature)</span>}
                                    {feature.disabled ? ' (not included)' : ''}
                                    {feature.info?.trim() && feature.info_icon === true && <FeatureInfo text={feature.info} />}
                                </Typography>
                            </Box>
                        ))}
                        {(plan.features || []).length === 0 && (
                            <Typography sx={{ fontSize: '1.035rem', color: '#adb5bd' }}>
                                No features yet.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default PlanCardPreview;
